import { Product } from '@interfaces';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import {
  extractConcentrationsLabs,
  extractUnits,
} from '../utils/extractExtraInfo';
import {
  InfoTagDeliveryDate,
  InfoTagLocalSell,
  InfoTagOnDemand,
  InfoTagSoldOut,
  InfoTagDiscountType,
} from './InfoTag';
import { ProductImage } from './ProductImage';
import { useAuth } from '@hooks/useAuth';
import {
  getPillBoxDetail,
  GetPillBoxDetailQueryParams,
  GetPillBoxDetailResponseQuery,
} from '@queries/pillBox/queries';
import { useQuery } from '@apollo/client';
import { ActionContainer } from './ActionContainer';
import { useState } from 'react';
import {
  Container,
  ContentContainer,
  ProductName,
  ActivePrinciple,
  Concentration,
  PriceContainer,
  Price,
  AveragePrice,
  LastBuyContainer,
  LastBuyText,
  LastBuyTextBold,
  DiscountPriceContainer,
  PricesContainer,
  DiscountPrice,
  DiscountChip,
  DiscountChipTypography,
  OriginalPrice,
  Overlay,
  InfoRightContainer,
  ProductDataContainer,
} from './Info.styles';
import { RefrigeratedInfo } from './RefrigeratedInfo';
const currencyFormatter = new CurrencyFormatter({ fallback: '' });
const extractAveragePriceText = (product: Product) => {
  const { unit, price: regularPrice, quantity } = product;
  const price = product.discount?.price
    ? product.discount?.price ?? regularPrice
    : regularPrice;
  if (!price) return null;
  if (/comprimido/.exec(unit)) {
    return `Valor comprimido: ${currencyFormatter.format(price / +quantity)}`;
  }
  if (/cápsula/.exec(unit)) {
    return `Valor cápsula: ${currencyFormatter.format(price / +quantity)}`;
  }
  if (/ml/.exec(unit)) {
    return `Valor cada 100 ml: ${currencyFormatter.format(
      (100 * price) / +quantity,
    )}`;
  }
  if (/g/.exec(unit)) {
    return `Valor cada 100 g: ${currencyFormatter.format(
      (100 * price) / +quantity,
    )}`;
  }
};

interface InfoProps {
  product: Product;
}

export const Info = ({ product }: InfoProps) => {
  const { appUser } = useAuth();

  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const { data: { pillBoxDetail } = {} } = useQuery<
    GetPillBoxDetailResponseQuery,
    GetPillBoxDetailQueryParams
  >(getPillBoxDetail(), {
    variables: {
      params: { productSlug: product?.slug },
    },
    skip: !product?.slug || !appUser,
    fetchPolicy: 'cache-only',
  });

  const handleSelectFocus = () => {
    setIsSelectOpen(true);
  };

  const handleSelectBlur = () => {
    setIsSelectOpen(false);
  };

  const isRefrigerated = product?.metadata?.refrigerated;
  const originalPrice =
    product.discount?.price != null
      ? (product.discount?.listPrice as number)
      : product.discount?.price ?? product.price;
  const discountPercentageRounded = Math.round(
    ((originalPrice - product.discount?.price) / originalPrice) * 100,
  );

  // show date as esp: DD/MM/YYYY
  const lastOrderDate = new Date(
    pillBoxDetail?.lastOrderDate ?? 0,
  ).toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <>
      {pillBoxDetail && (
        <LastBuyContainer>
          <LastBuyText>
            Comprado el
            <LastBuyTextBold> {lastOrderDate}</LastBuyTextBold>
          </LastBuyText>
        </LastBuyContainer>
      )}
      <Container>
        <ProductImage product={product} />
        <InfoRightContainer>
          <ContentContainer>
            <ProductDataContainer>
              <ProductName>{product.name}</ProductName>
              {product.activePrinciple?.split(';').length <= 3 ? (
                <ActivePrinciple>
                  {product.activePrinciple
                    .split(';')
                    .slice(0, 3)
                    .map((text) => text.trim().replace(/\(.*\)/g, ''))
                    .join(' ; ')}
                </ActivePrinciple>
              ) : null}
              <Concentration>
                {extractConcentrationsLabs(product)}
              </Concentration>
              <Concentration>{extractUnits(product)}</Concentration>
              <PriceContainer>
                {product.discount?.price &&
                !['SOLD_OUT', 'HIDDEN', 'UNAVAILABLE'].includes(
                  product.availability?.status,
                ) ? (
                  <DiscountPriceContainer>
                    <PricesContainer>
                      <DiscountPrice>
                        {currencyFormatter.format(product.discount?.price)}
                      </DiscountPrice>
                      <OriginalPrice>
                        {currencyFormatter.format(
                          (product.discount?.listPrice as number) ??
                            product.price,
                        )}
                      </OriginalPrice>
                    </PricesContainer>
                    {discountPercentageRounded >= 100 && (
                      <DiscountChip>
                        <DiscountChipTypography>
                          -{discountPercentageRounded}%
                        </DiscountChipTypography>
                      </DiscountChip>
                    )}
                  </DiscountPriceContainer>
                ) : (
                  <Price>{currencyFormatter.format(product.price)}</Price>
                )}

                <AveragePrice display="inline">
                  {extractAveragePriceText(product)}
                </AveragePrice>
              </PriceContainer>
            </ProductDataContainer>
            {['AVAILABLE', 'REPRESENTATION', 'AVAILABLE_24_HRS'].includes(
              product.availability?.status,
            ) &&
              product.prescriptionType !== 'CHECK' && (
                <InfoTagDeliveryDate
                  product={product}
                  maxQuantity={
                    product.availability.stock > 5 ||
                    product.prescriptionType === 'HELD'
                      ? undefined
                      : Math.max(
                          product.discount?.stock ?? product.availability.stock,
                          0,
                        )
                  }
                />
              )}
            {['EXPIRATION', 'MEKI'].includes(product.discount?.type) &&
              !['SOLD_OUT', 'HIDDEN', 'UNAVAILABLE'].includes(
                product.availability?.status,
              ) && <InfoTagDiscountType type={product.discount.type} />}
            {product.availability?.status == 'TO_CONFIRM' && (
              <InfoTagOnDemand />
            )}
            {['SOLD_OUT', 'UNAVAILABLE', 'HIDDEN'].includes(
              product.availability?.status,
            ) && <InfoTagSoldOut />}
            {product.prescriptionType === 'CHECK' && <InfoTagLocalSell />}
            {isRefrigerated && <RefrigeratedInfo />}
          </ContentContainer>
          <ActionContainer
            product={product}
            isSelectOpen={isSelectOpen}
            handleSelectFocus={handleSelectFocus}
            handleSelectBlur={handleSelectBlur}
          />
        </InfoRightContainer>
      </Container>
      {isSelectOpen && <Overlay />}
    </>
  );
};
